import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import {
  type CustomFieldInput,
  CustomFieldObjectTypes,
  SubStepTypes,
  WorkflowObjectTypes,
} from '@generated/graphql';
import { type CustomField, FETCH_CUSTOM_FIELDS } from '@interfaces/customField';
import {
  ACCOUNT_RULE_FIELDS,
  ENTITY_RULE_FIELDS,
  GOAL_RULE_FIELDS,
} from '@ovComponents/4-module/widgets/customFieldsDisplay/customFieldsDisplay.queries';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { useContext, useState } from 'react';
import { usePermission } from '../../../../../hooks/usePermission';
import { getEnabledCustomFields } from '../utils';
import {
  FETCH_HOUSEHOLD_CUSTOM_FIELDS,
  UPDATE_HOUSEHOLD_CUSTOM_FIELDS,
} from './approval.query';

const FETCH_USER = (derivedCustomFields?: string[]) => gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        ${ENTITY_RULE_FIELDS}
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format objectType }
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;
const FETCH_ACCOUNT = (derivedCustomFields?: string[]) => gql`
  query fetchAccount($accountId: SafeString!) {
    fetchAccount(input: { accountId: $accountId }) {
      account {
        id: stringId
        ${ACCOUNT_RULE_FIELDS}
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format objectType }
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

const FETCH_GOAL = (derivedCustomFields?: string[]) => gql`
  query fetchGoal($goalId: ObjectID!) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        ${GOAL_RULE_FIELDS}
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format objectType }
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;
const UPDATE_GOAL = gql`
  mutation updateGoal($input: UpdateGoalInput!) {
    updateGoal(input: $input) {
      goal {
        id
      }
    }
  }
`;
const UPDATE_ACCOUNT = gql`
  mutation updateAccount($input: UpdateAccountInput!) {
    updateAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

type CustomFieldUpdate = { key: string; value?: unknown };

type StoredCustomField = CustomFieldUpdate & {
  customField?: { objectType?: CustomFieldObjectTypes | null } | null;
};

type CustomFieldOption = {
  customField: { key: string; objectType: CustomFieldObjectTypes };
};

type CustomFieldTarget = {
  objectType: CustomFieldObjectTypes;
  recordId?: string;
  load: (recordId: string, keys: string[]) => Promise<unknown>;
  save: (
    recordId: string,
    customFields: CustomFieldUpdate[],
  ) => Promise<unknown>;
};

export type UseApprovalCustomFieldsInput = {
  options: any;
  onNext: (branchId?: string) => void;
  objectId?: string;
  objectType?: WorkflowObjectTypes;
};

const optionKeysOfType = (
  customOptions: CustomFieldOption[],
  objectType: CustomFieldObjectTypes,
) =>
  customOptions
    .filter((option) => option.customField.objectType === objectType)
    .map((option) => option.customField.key);

const storedKeysOfType = (
  storedCustomFields: StoredCustomField[],
  objectType: CustomFieldObjectTypes,
) =>
  storedCustomFields
    .filter((field) => field.customField?.objectType === objectType)
    .map((field) => field.key);

// The whole fetched record is kept so custom-field trigger rules can read its
// attributes (e.g. account `type` === RLIF). `id` is remapped per record so
// merging several of them doesn't leave them fighting over it.
const mergeObjects =
  (object: any, idKey: 'userId' | 'goalId' | 'accountId' | 'clientGroupId') =>
  (prev: any) => {
    if (!object) return prev;
    const { id, customFields = [], ...rest } = object;
    return {
      ...prev,
      ...rest,
      [idKey]: id,
      customFields: [...prev.customFields, ...customFields],
    };
  };

export const useApprovalCustomFields = ({
  options,
  onNext,
  objectId,
  objectType,
}: UseApprovalCustomFieldsInput) => {
  const { activeOrganization } = useContext(UserContext);
  const { workflowData, userId: ownerId } = useContext(WorkflowContext);
  const [derivedCustomFields, setDerivedCustomFields] = useState<CustomField[]>(
    [],
  );
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] = useState<
    string[]
  >([]);
  const enabledCustomFields = getEnabledCustomFields(options);
  const canReadCustomFields = usePermission('read:custom_fields');
  const userId =
    workflowData.currentUserId ||
    ownerId ||
    (objectType === WorkflowObjectTypes.User ? objectId : undefined);
  const goalId =
    workflowData.currentGoalId ||
    (objectType === WorkflowObjectTypes.Goal ? objectId : undefined);
  const accountId =
    workflowData.currentAccountId ||
    (objectType === WorkflowObjectTypes.Account ? objectId : undefined);
  // "Household in context" branch conditions read the completion's own client
  // group, so household fields only load from and save to that record.
  const clientGroupId =
    objectType === WorkflowObjectTypes.Household ? objectId : undefined;
  const [approvalData, setApprovalData] = useState<any>({
    customFields: [],
    userId: '',
    goalId: '',
    accountId: '',
    clientGroupId: '',
  });
  const { loading: customFieldsLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.Approval,
          organizationId: activeOrganization?.id ?? undefined,
          keys: enabledCustomFields,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !canReadCustomFields || enabledCustomFields.length === 0,
    fetchPolicy: 'no-cache',
    onCompleted: async (res: any) => {
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      const customOptions: CustomFieldOption[] = [];
      const keyItems = res?.fetchCustomFields?.customFields.map(
        (a: CustomField) => a.key,
      );
      Object.keys(options).forEach((key: string) => {
        if (
          options[key]?.customField &&
          options[key].enabled &&
          keyItems?.includes(key)
        ) {
          customOptions.push(options[key]);
        }
      });

      const newCustomFieldsKeys = [...derivedCustomFieldsKeys];
      for (const {
        objectType: targetType,
        recordId,
        load,
      } of customFieldTargets) {
        const keys = optionKeysOfType(customOptions, targetType);
        if (!recordId || keys.length === 0) continue;
        newCustomFieldsKeys.push(...keys);
        await load(recordId, keys);
      }
      setDerivedCustomFieldsKeys(newCustomFieldsKeys);
    },
  });

  const [fetchUser, { loading: userLoading }] = useLazyQuery(FETCH_USER(), {
    variables: { userId: workflowData.currentUserId },
    fetchPolicy: 'no-cache',
  });

  const [fetchGoal, { loading: goalLoading }] = useLazyQuery(FETCH_GOAL(), {
    variables: { goalId: workflowData.currentGoalId },
    fetchPolicy: 'no-cache',
  });

  const [fetchAccount, { loading: accountLoading }] = useLazyQuery(
    FETCH_ACCOUNT(),
    {
      variables: { accountId: workflowData.currentAccountId },
      fetchPolicy: 'no-cache',
    },
  );

  const [fetchHousehold, { loading: householdLoading }] = useLazyQuery(
    FETCH_HOUSEHOLD_CUSTOM_FIELDS,
    { fetchPolicy: 'no-cache' },
  );

  const [updateUser] = useMutation(UPDATE_USER);

  const [updateGoal] = useMutation(UPDATE_GOAL);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT);

  const [updateHousehold] = useMutation(UPDATE_HOUSEHOLD_CUSTOM_FIELDS);

  const customFieldTargets: CustomFieldTarget[] = [
    {
      objectType: CustomFieldObjectTypes.Entity,
      recordId: userId,
      load: (recordId, keys) =>
        fetchUser({
          variables: { userId: recordId },
          query: FETCH_USER(keys),
          onCompleted: (res) =>
            setApprovalData(mergeObjects(res.fetchUser?.user, 'userId')),
        }),
      save: (recordId, customFields) =>
        updateUser({
          variables: { input: { userId: recordId, customFields } },
        }),
    },
    {
      objectType: CustomFieldObjectTypes.Goal,
      recordId: goalId,
      load: (recordId, keys) =>
        fetchGoal({
          variables: { goalId: recordId },
          query: FETCH_GOAL(keys),
          onCompleted: (res) =>
            setApprovalData(mergeObjects(res.fetchGoal?.goal, 'goalId')),
        }),
      save: (recordId, customFields) =>
        updateGoal({
          variables: { input: { goalId: recordId, customFields } },
        }),
    },
    {
      objectType: CustomFieldObjectTypes.Account,
      recordId: accountId,
      load: (recordId, keys) =>
        fetchAccount({
          variables: { accountId: recordId },
          query: FETCH_ACCOUNT(keys),
          onCompleted: (res) =>
            setApprovalData(
              mergeObjects(res.fetchAccount?.account, 'accountId'),
            ),
        }),
      save: (recordId, customFields) =>
        updateAccount({
          variables: { input: { accountId: recordId, customFields } },
        }),
    },
    {
      objectType: CustomFieldObjectTypes.Household,
      recordId: clientGroupId,
      load: (recordId, keys) =>
        fetchHousehold({
          variables: { clientGroupId: recordId, keys },
          onCompleted: (res) =>
            setApprovalData(
              mergeObjects(res.fetchClientGroup?.clientGroup, 'clientGroupId'),
            ),
        }),
      save: (recordId, customFields) =>
        updateHousehold({
          variables: {
            input: {
              clientGroupId: recordId,
              // Codegen types the JSON scalar as Record<string, unknown>, but
              // custom field values are also strings, numbers, and booleans.
              customFields: customFields as CustomFieldInput[],
            },
          },
        }),
    },
  ];

  const saveCustomFieldsAndContinue = async (branchId: string) => {
    const storedCustomFields: StoredCustomField[] =
      approvalData?.customFields ?? [];
    // Saved before onNext: the next sub-step's branch conditions read these
    // values back from the saved records.
    for (const {
      objectType: targetType,
      recordId,
      save,
    } of customFieldTargets) {
      const keys = storedKeysOfType(storedCustomFields, targetType);
      if (!recordId || keys.length === 0) continue;
      await save(
        recordId,
        modifyUpdatingCustomFields(
          storedCustomFields,
          derivedCustomFields ?? [],
          keys,
        ),
      );
    }
    await onNext(branchId);
  };

  return {
    approvalData,
    setApprovalData,
    activeCustomFields: derivedCustomFieldsKeys,
    derivedCustomFields,
    loading:
      userLoading ||
      goalLoading ||
      accountLoading ||
      householdLoading ||
      customFieldsLoading,
    saveCustomFieldsAndContinue,
  };
};
